import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowRight, Shield, Sparkle, Clock, Target, TrendUp, Rocket } from '@phosphor-icons/react';
import { createCheckoutSession } from '@/utils/stripe';
import { isAuthenticated, clearAuth, getUser } from '@/utils/auth';
import api from '@/utils/api';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const PricingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(null);

  // 2026-05-31 — P0 billing fix.
  // If the user bailed out of Stripe Checkout AFTER signing up
  // (their account is still in `pending_payment` state with no
  // Stripe subscription on file), delete the half-created row so
  // their email is free to retry. Hides the canceled cleanup
  // detail from the user — they just see the cancel toast.
  React.useEffect(() => {
    const wasCanceled = searchParams.get('canceled') === 'true';
    const wasRequired = searchParams.get('required') === '1';
    const pendingSignup = localStorage.getItem('paygbot_pending_signup') === '1';

    if (wasCanceled) {
      toast.info('Checkout canceled. You can subscribe anytime!');
    }
    if (wasRequired) {
      toast.info('Please complete checkout to access PayGBot.');
    }

    if (wasCanceled && pendingSignup && isAuthenticated()) {
      const user = getUser() || {};
      // Only clean up pending_payment rows; never an internal account.
      if (!user.is_internal && (user.subscription_status === 'pending_payment' || !user.subscription_status)) {
        api.post('/auth/cancel-pending-signup')
          .catch(() => { /* non-fatal — backend defends idempotently */ })
          .finally(() => {
            localStorage.removeItem('paygbot_pending_signup');
            clearAuth();
          });
      }
    }
  }, [searchParams]);

  const handleSubscribe = async (plan) => {
    if (!isAuthenticated()) {
      toast.info('Please sign up first to subscribe');
      navigate(`/signup?redirect=pricing&plan=${plan}`);
      return;
    }
    
    setLoading(plan);
    await createCheckoutSession(plan);
    setLoading(null);
  };

  // Phase G (2026-02-06) — Under the unified V2 Business Strategist
  // model there is ONE plan for every paying customer. The legacy
  // Growth ($99) / Pro ($199) Stripe Prices have been retired: all
  // three STRIPE_PRICE_* env vars now point to the same Business
  // Strategist product ID.
  const plans = [
    {
      id: 'starter', // internal key retained; Stripe Price is unchanged.
      name: 'Business Strategist',
      price: 49,
      period: 'month',
      headline: 'Your AI Business Strategist — always on, always thinking.',
      description: 'One subscription. Continuous strategy, planning, and drafts. Business Credits power the finished production.',
      trial: true,
      trialDays: 7,
      isTrialPlan: true,
      benefits: [
        { text: 'Unlimited strategy, planning, drafts & revisions', included: true, highlight: true },
        { text: 'Business Brain, Brand Brain, Market & Opportunity Intelligence', included: true, highlight: true },
        { text: 'Daily Missions + Weekly Planner', included: true },
        { text: '30 Business Credits every month for finished production', included: true, highlight: true },
        { text: 'Purchase additional Business Credits any time', included: true },
      ],
      cta: 'Start Free Trial',
      ctaSubtext: '7 days free, then $49/month',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">P</span>
              </div>
              <span className="text-xl font-bold text-white">PayGBot</span>
            </Link>
            <div className="flex items-center space-x-4">
              {isAuthenticated() ? (
                <Link to="/dashboard" className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
                  Dashboard
                </Link>
              ) : (
                <>
                  <Link to="/login" className="text-sm font-medium text-slate-300 hover:text-white">
                    Login
                  </Link>
                  <Link to="/signup" className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
                    Sign Up
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center space-y-4 mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white" data-testid="pricing-title">
              Build Your Business, Not Just Content
            </h1>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              PayGBot is a system that guides you daily. Pick the level that fits where you are.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative rounded-2xl p-8 ${
                  plan.popular 
                    ? 'bg-gradient-to-b from-purple-500/20 to-blue-500/20 border-2 border-purple-500' 
                    : 'bg-white/10 border border-white/20'
                }`}
                data-testid={`pricing-card-${plan.id}`}
              >
                {/* Popular Badge */}
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-purple-600 text-white text-sm font-semibold px-4 py-1 rounded-full flex items-center space-x-1">
                      <Sparkle size={14} weight="fill" />
                      <span>Most Popular</span>
                    </span>
                  </div>
                )}

                {/* Plan Header */}
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-white mb-2">{plan.name}</h2>
                  <p className="text-white font-medium mb-1">{plan.headline}</p>
                  <p className="text-slate-400 text-sm mb-4">{plan.description}</p>
                  <div className="flex items-baseline justify-center space-x-1">
                    <span className="text-5xl font-bold text-white">${plan.price}</span>
                    <span className="text-slate-400">/{plan.period}</span>
                  </div>
                  {plan.trial ? (
                    <p className="text-emerald-400 font-medium mt-2 text-sm">
                      {plan.trialDays}-day free trial
                    </p>
                  ) : (
                    <p className="text-slate-400 mt-2 text-sm">
                      No trial — billed immediately
                    </p>
                  )}
                </div>

                {/* Benefits */}
                <ul className="space-y-3 mb-8">
                  {plan.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <CheckCircle 
                        size={18} 
                        weight="fill" 
                        className={`flex-shrink-0 mt-0.5 ${benefit.highlight ? 'text-purple-400' : 'text-emerald-400'}`} 
                      />
                      <span className={benefit.highlight ? 'text-purple-300' : 'text-white'}>
                        {benefit.text}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={loading === plan.id}
                  data-testid={`subscribe-${plan.id}-btn`}
                  className={`w-full py-4 font-semibold rounded-xl transition-colors disabled:opacity-50 flex flex-col items-center justify-center ${
                    plan.popular
                      ? 'bg-purple-600 hover:bg-purple-700 text-white'
                      : 'bg-white/10 hover:bg-white/20 text-white border border-white/20'
                  }`}
                >
                  {loading === plan.id ? (
                    <span>Loading...</span>
                  ) : (
                    <>
                      <span className="flex items-center space-x-2">
                        <span>{plan.cta}</span>
                        <ArrowRight size={18} />
                      </span>
                      {plan.ctaSubtext && (
                        <span className="text-xs opacity-70 mt-1">{plan.ctaSubtext}</span>
                      )}
                    </>
                  )}
                </button>
              </motion.div>
            ))}
          </div>

          {/* Recommendation Line */}
          <div className="mt-8 text-center">
            <p className="text-slate-300 text-lg">
              <span className="text-purple-400 font-medium">Start your 7-day free trial</span> — then continue on Business Strategist for $49/month
            </p>
          </div>

          {/* Trust Indicators */}
          <div className="mt-12 flex flex-wrap items-center justify-center gap-8 text-slate-400">
            <div className="flex items-center space-x-2">
              <Shield size={20} />
              <span>Secure payment via Stripe</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle size={20} />
              <span>Cancel anytime</span>
            </div>
            <div className="flex items-center space-x-2">
              <Clock size={20} />
              <span>No charge until trial ends</span>
            </div>
          </div>

          {/* What You Get Section */}
          <div className="mt-20 max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white text-center mb-8">What You're Really Getting</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <Target size={24} className="text-blue-400" weight="fill" />
                </div>
                <h3 className="font-semibold text-white mb-2">Daily Clarity</h3>
                <p className="text-slate-400 text-sm">
                  Know exactly what to do every day without guessing or overthinking
                </p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <TrendUp size={24} className="text-purple-400" weight="fill" />
                </div>
                <h3 className="font-semibold text-white mb-2">Real Progress</h3>
                <p className="text-slate-400 text-sm">
                  Build something real — an audience, a product, or a business that earns
                </p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <Rocket size={24} className="text-emerald-400" weight="fill" />
                </div>
                <h3 className="font-semibold text-white mb-2">Consistent Action</h3>
                <p className="text-slate-400 text-sm">
                  A system that keeps you moving forward, even on hard days
                </p>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-20 max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white text-center mb-8">Frequently Asked Questions</h2>
            <div className="space-y-4">
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">What am I paying for?</h3>
                <p className="text-slate-400">
                  Your AI Business Strategist — continuous strategy, planning, market research, and drafting across your entire business. Unlimited use.
                </p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">What are Business Credits?</h3>
                <p className="text-slate-400">
                  Business Credits power professional production — the finished sales pages, lead magnets, ebooks, logos, and videos your strategist ships when you approve them. Every subscription includes 30 credits per month. Buy more any time.
                </p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Do unused credits roll over?</h3>
                <p className="text-slate-400">
                  Yes — monthly credits roll over up to 2× your monthly allowance. Purchased credits never expire while your subscription is active.
                </p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">How does the free trial work?</h3>
                <p className="text-slate-400">
                  Start your 7-day free trial today. Explore the full strategist — strategy, planning, drafting, revisions — everything except finished-production downloads. After the trial, continue for $49/month.
                </p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Can I cancel any time?</h3>
                <p className="text-slate-400">
                  Yes. Cancel any time from your billing settings. Purchased Business Credits remain available for 90 days after cancellation.
                </p>
              </div>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="mt-20 text-center">
            <p className="text-slate-300 mb-4">Ready to put a strategist to work on your business?</p>
            <button
              onClick={() => handleSubscribe('starter')}
              disabled={loading === 'starter'}
              data-testid="bottom-cta-strategist-btn"
              className="inline-flex items-center space-x-2 px-8 py-4 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl transition-colors disabled:opacity-50"
            >
              <span>Start Your Free Trial</span>
              <ArrowRight size={20} />
            </button>
            <p className="text-slate-500 text-sm mt-3">7 days free, then $49/month · Cancel any time</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-white/10 text-slate-400 text-center">
        <p>&copy; 2026 PayGBot. Build your online business in minutes a day.</p>
      </footer>
    </div>
  );
};

export default PricingPage;
